import { useEffect, useState } from 'react'
import { Area, AreaChart, CartesianGrid, LabelList, XAxis } from 'recharts'
import {
  Users,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
  BadgeCheck,
  CircleSlash,
  MapPin,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/shared/components/ui/badge'
import {
  useRegistrosSueroterapia,
  useRegistrosPorDiaSueroterapia,
  useRegistrosPorProvinciaSueroterapia,
} from '../hooks/useSueroterapia'
import { MapaRegiones } from '@/modules/reportes-fidelizacion/components/charts/MapaRegiones'
import { AZUL, VERDE, KpiCard } from './KpiCard'

const PAGE_SIZE = 25

const chartConfig = {
  registros: {
    label: 'Registros',
    color: AZUL,
  },
} satisfies ChartConfig

function fechaHora(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('es-EC', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

interface RegistrosTabProps {
  dias: number
}

export function RegistrosTab({ dias }: RegistrosTabProps) {
  const [offset, setOffset] = useState(0)

  // Al cambiar el período, volver a la primera página.
  useEffect(() => {
    setOffset(0)
  }, [dias])

  const { data: serieData, isLoading: cargandoSerie } = useRegistrosPorDiaSueroterapia(dias)
  const {
    data: provinciaData,
    isLoading: cargandoProvincia,
    error: errorProvincia,
  } = useRegistrosPorProvinciaSueroterapia(dias)
  const {
    data: pagina,
    isLoading: cargandoTabla,
    error: errorTabla,
  } = useRegistrosSueroterapia(dias, PAGE_SIZE, offset)

  const nf = (n: number) => n.toLocaleString('es-EC')

  // Datos del mapa: {label: provincia, value: total}. Se excluye "Sin provincia"
  // (no coincide con ninguna región del geojson de Ecuador).
  const mapaData = (provinciaData?.provincias ?? [])
    .filter((p) => p.provincia !== 'Sin provincia' && p.total > 0)
    .map((p) => ({ label: p.provincia, value: p.total }))
  const totalProvincias = mapaData.length

  const areaChartData =
    serieData?.serie?.map((p) => ({ fecha: p.fecha, registros: p.total })) ?? []

  const total = pagina?.total ?? serieData?.total_registros ?? 0
  const promedioDiario = dias > 0 ? total / dias : 0

  const desdeVisible = total === 0 ? 0 : offset + 1
  const hastaVisible = Math.min(offset + PAGE_SIZE, total)
  const hayAnterior = offset > 0
  const haySiguiente = offset + PAGE_SIZE < total

  return (
    <div className="space-y-5">
      {/* KPIs + mapa de provincias en la misma fila */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 content-start">
          <KpiCard
            icon={Users}
            label="Cantidad de registros"
            value={nf(total)}
            sub="Usuarios registrados en el período"
            color={AZUL}
          />
          <KpiCard
            icon={CalendarDays}
            label="Promedio diario"
            value={promedioDiario.toFixed(1)}
            sub={`Sobre ${dias} días`}
            color={VERDE}
          />
        </div>
        <MapaRegiones
          title="Registros por provincia"
          data={mapaData}
          colorNombre="blue"
          isLoading={cargandoProvincia}
          error={errorProvincia}
          icon={MapPin}
          headerStat={{ value: totalProvincias, label: 'provincias' }}
          aspectRatio="460 / 170"
        />
      </div>

      {/* Gráfica de registros por día */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Registros por día</CardTitle>
        </CardHeader>
        <CardContent>
          {cargandoSerie ? (
            <div className="flex items-center justify-center h-[250px]">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : areaChartData.length > 0 ? (
            <ChartContainer config={chartConfig} className="!aspect-auto h-[250px] w-full">
              <AreaChart
                accessibilityLayer
                data={areaChartData}
                margin={{ left: 12, right: 12, top: 20 }}
              >
                <defs>
                  <linearGradient id="fillRegistrosSuero" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-registros)" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="var(--color-registros)" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="fecha"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={24}
                  tickFormatter={(value) => {
                    const date = new Date(value)
                    return date.toLocaleDateString('es-EC', { day: '2-digit', month: 'short' })
                  }}
                />
                <ChartTooltip
                  cursor={{ stroke: AZUL, strokeWidth: 1, strokeDasharray: '4 4' }}
                  content={<ChartTooltipContent indicator="line" />}
                />
                <Area
                  dataKey="registros"
                  type="natural"
                  fill="url(#fillRegistrosSuero)"
                  fillOpacity={1}
                  stroke="var(--color-registros)"
                  strokeWidth={2}
                >
                  <LabelList
                    dataKey="registros"
                    position="top"
                    offset={10}
                    className="fill-foreground"
                    fontSize={11}
                    formatter={(value: number) => (value > 0 ? value : '')}
                  />
                </Area>
              </AreaChart>
            </ChartContainer>
          ) : (
            <div className="flex items-center justify-center h-[250px] text-muted-foreground">
              <div className="text-center space-y-2">
                <CircleSlash className="h-12 w-12 mx-auto" />
                <p>Sin registros en el período</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabla de registros */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <div className="flex flex-col gap-1">
            <CardTitle className="text-sm font-medium">Detalle de registros</CardTitle>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <ShieldAlert className="h-3 w-3" /> Contiene datos personales (PII); trátelos con
              confidencialidad.
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {cargandoTabla ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : errorTabla ? (
            <p className="text-destructive text-sm py-8 text-center">
              No se pudieron cargar los registros. Verifique la configuración e intente de nuevo.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] hover:bg-gray-100 dark:hover:bg-[hsl(220,15%,16%)]">
                      <TableHead>Email</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Provincia</TableHead>
                      <TableHead>Teléfono</TableHead>
                      <TableHead>Especialidad</TableHead>
                      <TableHead>Verificado</TableHead>
                      <TableHead>Fecha registro</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagina && pagina.items.length > 0 ? (
                      pagina.items.map((r, idx) => (
                        <TableRow key={`${r.email}-${idx}`}>
                          <TableCell className="font-medium">{r.email}</TableCell>
                          <TableCell>{`${r.first_name} ${r.last_name}`.trim() || '—'}</TableCell>
                          <TableCell>{r.province || '—'}</TableCell>
                          <TableCell>{r.phone || '—'}</TableCell>
                          <TableCell>{r.specialty || '—'}</TableCell>
                          <TableCell>
                            {r.email_verified ? (
                              <Badge variant="outline" className="text-xs flex w-fit items-center gap-1">
                                <BadgeCheck className="h-3 w-3 text-green-500" /> Sí
                              </Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">No</span>
                            )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">{fechaHora(r.date_joined)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-10">
                          Sin registros en el período.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Paginación */}
              <div className="flex items-center justify-between mt-4">
                <span className="text-xs text-muted-foreground">
                  {total > 0 ? `${desdeVisible}–${hastaVisible} de ${nf(total)}` : '0 registros'}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                    disabled={!hayAnterior}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:pointer-events-none"
                  >
                    <ChevronLeft className="h-4 w-4" /> Anterior
                  </button>
                  <button
                    onClick={() => setOffset((o) => o + PAGE_SIZE)}
                    disabled={!haySiguiente}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:pointer-events-none"
                  >
                    Siguiente <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
